import { Injectable } from '@angular/core';
import { Erzeugungsanlage } from '../../../domain/model/erzeugungsanlage';
import { ErzeugungsanlageFilter } from '../../erzeugungsanlage/erzeugungsanlage-filter';
import { Netzbetreiber } from '../../../domain/model/netzbetreiber';
import { NetzzuordnungFilter } from '../../netzzuordnung/netzzuordnung-filter';
import { Pageable } from '../../../common/paging/pageable';

@Injectable({
    providedIn: 'root',
})
export class RoutingService {

    public static readonly ERZEUGUNGSANLAGE = 'erzeugungsanlage';
    public static readonly NETZBETREIBER = 'netzbetreiber';
    public static readonly NETZZUORDNUNG = 'netzzuordnung';

    public dashboard() {
        return '/';
    }

    public erzeugungsanlageTable(pageable?: Pageable, erzeugungsanlageFilter?: ErzeugungsanlageFilter): string {
        return '/' + RoutingService.ERZEUGUNGSANLAGE + '/list?' +
            this.pageableToQuery(pageable,
                this.filterErzeugungsanlageToQuery(erzeugungsanlageFilter))
                .toString();
    }

    public netzzuordnungTable(pageable?: Pageable, netzzuordnungFilter?: NetzzuordnungFilter): string {
        return '/' + RoutingService.NETZZUORDNUNG + '/list?' +
            this.pageableToQuery(pageable,
                this.filterNetzzuordnungenToQuery(netzzuordnungFilter))
                .toString();
    }

    public erzeugungsanlagePath(): string {
        return '/' + RoutingService.ERZEUGUNGSANLAGE + '/list';
    }

    public netzbetreiberPath(): string {
        return '/' + RoutingService.NETZBETREIBER + '/list';
    }

    public netzbetreiberTable(pageable?: Pageable, filter?: string): string {
        return `${this.netzbetreiberPath()}?` +
            this.pageableToQuery(pageable,
                this.filterNetzbetreiberToQuery(filter))
                .toString();
    }

    public netzzuordnungPath(): string {
        return '/' + RoutingService.NETZZUORDNUNG + '/list';
    }

    public erzeugungsanlage(erzeugungsanlage: Erzeugungsanlage, pageable?: Pageable): string {
        return this.erzeugungsanlageByWestnetzId(erzeugungsanlage.westnetzId, pageable);
    }

    public netzbetreiber(netzbetreiber: Netzbetreiber): string {
        return this.netzbetreiberByStadtwerkeId(netzbetreiber.stadtwerkeId);
    }

    public erzeugungsanlageByWestnetzId(westnetzId: string, pageable?: Pageable): string {
        const queryParams = this.pageableToQuery(pageable);
        queryParams.append('erzeugungsanlage', westnetzId);

        return this.erzeugungsanlagePath() + '?' + queryParams.toString();
    }

    public netzbetreiberByStadtwerkeId(stadtwerkeId: string): string {
        const queryParams = new URLSearchParams();
        queryParams.append('stadtwerkeId', stadtwerkeId);

        return this.netzbetreiberPath() + '?' + queryParams.toString();
    }

    private pageableToQuery(pageable?: Pageable, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (pageable) {
            if (pageable.page) {
                urlSearchParams.append('page', pageable.page + '');
            }
            if (pageable.sort) {
                urlSearchParams.append('sort', pageable.sort);
            }
            if (pageable.direction) {
                urlSearchParams.append('direction', pageable.direction);
            }
        }
        return urlSearchParams;
    }

    private filterErzeugungsanlageToQuery(filter?: ErzeugungsanlageFilter, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (filter) {
            if (filter.westnetzId) {
                urlSearchParams.append('westnetzId', '' + filter.westnetzId);
            }
            if (typeof filter.installierteLeistungMin == 'number') {
                urlSearchParams.append('installierteLeistungMin', '' + filter.installierteLeistungMin);
            }
            if (typeof filter.installierteLeistungMax == 'number') {
                urlSearchParams.append('installierteLeistungMax', '' + filter.installierteLeistungMax);
            }
            if (filter.anlagentype) {
                urlSearchParams.append('anlagentype', filter.anlagentype);
            }
            if (filter.ortsnamen) {
                urlSearchParams.append('ortsnamen', filter.ortsnamen);
            }
            if (filter.stationsnamen) {
                urlSearchParams.append('stationsnamen', filter.stationsnamen);
            }
            if (filter.netzbetreiberName) {
                urlSearchParams.append('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.netzbetreiberTeilgebiet) {
                urlSearchParams.append('netzbetreiberTeilgebiet', filter.netzbetreiberTeilgebiet);
            }
            if (filter.leitstellenRegion) {
                if (typeof filter.leitstellenRegion === 'string') {
                    urlSearchParams.append('leitstellenRegion', filter.leitstellenRegion);
                } else {
                    filter.leitstellenRegion.forEach(it => urlSearchParams.append('leitstellenRegion', it));
                }
            }
            if (typeof filter.status == 'boolean') {
                urlSearchParams.append('status', JSON.stringify(filter.status));
            }
        }
        return urlSearchParams;
    }

    private filterNetzzuordnungenToQuery(filter?: NetzzuordnungFilter, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (filter) {
            if (filter.trafoFeldName) {
                urlSearchParams.append('trafoFeldName', filter.trafoFeldName);
            }
            if (filter.ortsname) {
                urlSearchParams.append('ortsname', filter.ortsname);
            }
            if (filter.stationsname) {
                urlSearchParams.append('stationsname', filter.stationsname);
            }
            if (filter.uuid) {
                urlSearchParams.append('uuid', filter.uuid);
            }

            if (filter.uuidDateStart) {
                urlSearchParams.append('uuidDateStart', filter.uuidDateStart.toISOString(true));
            }
            if (filter.uuidDateEnd) {
                urlSearchParams.append('uuidDateEnd', filter.uuidDateEnd.toISOString(true));
            }
            if (filter.technischerPlatz) {
                urlSearchParams.append('technischerPlatz', filter.technischerPlatz);
            }
            if (filter.netzbetreiberName) {
                urlSearchParams.append('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.netzbetreiberTeilgebiet) {
                urlSearchParams.append('netzbetreiberTeilgebiet', filter.netzbetreiberTeilgebiet);
            }
            if (filter.stadtwerkeId) {
                urlSearchParams.append('stadtwerkeId', filter.stadtwerkeId);
            }
            if (filter.netzgruppe) {
                urlSearchParams.append('netzgruppe', filter.netzgruppe);
            }
        }
        return urlSearchParams;
    }

    private filterNetzbetreiberToQuery(filter?: string, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (filter) {
            urlSearchParams.append('filter', filter);
        }
        return urlSearchParams;
    }
}

